import React, { useCallback, useState } from "react";
import { AnnualClockQuarter } from "api-client";
import LinkButton from "components/Buttons/LinkButton";
import classNames from "classnames";
import { useIntl } from "react-intl";

import "./AnnualClockContainer.scss";

type OnChange = (e: React.MouseEvent<HTMLButtonElement>, newValue: string) => void;

export enum Position {
    Manager = "Manager",
    Employee = "Employee",
    HR = "HR",
}
interface SliceQuarter {
    value: string;
    selectedSlice: string;
    handleClick: OnChange;
    position?: Position;
}

const SliceQuarter = ({ value, selectedSlice, handleClick, position }: SliceQuarter) => {
    const active = value === selectedSlice;
    const onSliceClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        handleClick(e, value);
    };

    return (
        <li
            className={classNames("quarter", {
                "quarter--active": active,
            })}
        >
            <button
                id={value + position}
                key={value}
                onClick={onSliceClick}
                className={classNames("quarter--contents", {
                    "quarter--contents--employee--selected":
                        active && position === Position.Employee,
                    "quarter--contents--manager--selected":
                        active && (position === Position.Manager || position === Position.HR),
                })}
            >
                <span
                    className={classNames("quarter--contents--text", {
                        "quarter--contents--text--selected": active,
                    })}
                >
                    {value}
                </span>
            </button>
        </li>
    );
};
interface SliceMonth {
    value: string;
    label: string | number | undefined;
    selectedSlice: string;
    handleClick: OnChange;
    position?: Position;
    classes?: {
        root?: string;
        contents?: string;
        text?: string;
    };
}

const SliceMonth = ({
    value,
    selectedSlice,
    handleClick,
    label,
    classes,
    position,
}: SliceMonth) => {
    const active = value === selectedSlice;
    const onSliceClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        handleClick(e, value);
    };

    return (
        <li
            className={classNames("month", classes?.root, {
                "month--active": active,
            })}
        >
            <button
                id={value + position}
                key={label}
                onClick={onSliceClick}
                className={classNames(classes?.contents, "month--contents", {
                    "month--contents--employee--selected": active && position === Position.Employee,
                    "month--contents--manager--selected":
                        active && (position === Position.Manager || position === Position.HR),
                })}
            >
                <span
                    className={classNames("month--contents--text", classes?.text, {
                        "month--contents--text--selected": active,
                    })}
                >
                    {label}
                </span>
            </button>
        </li>
    );
};

interface AnnualClockPie {
    quarters: AnnualClockQuarter[];
    selectedQuarter: string;
    handleClick: OnChange;
    position?: Position;
}
const AnnualClockPie = ({ quarters, selectedQuarter, handleClick, position }: AnnualClockPie) => {
    return (
        <div className="layer">
            <ul className="week-level">
                {quarters.map((quarter) => {
                    const active = quarter.quarter === selectedQuarter;
                    return quarter.months.map((month) => {
                        return (
                            <SliceMonth
                                key={month.month}
                                classes={{
                                    root: "week",
                                    contents: classNames("week--contents", {
                                        "week--contents--employee--selected":
                                            active && position == Position.Employee,
                                        "week--contents--manager--selected":
                                            active &&
                                            (position == Position.Manager ||
                                                position == Position.HR),
                                    }),
                                    text: classNames("week--contents--text", {
                                        "week--contents--text--selected": active,
                                    }),
                                }}
                                value={quarter.quarter}
                                handleClick={handleClick}
                                selectedSlice={selectedQuarter}
                                label={month.events?.length}
                            ></SliceMonth>
                        );
                    });
                })}
            </ul>
            <ul className="month-level">
                {quarters.map((quarter) => {
                    return quarter.months.map((month) => {
                        return (
                            <SliceMonth
                                key={month.month}
                                position={position}
                                value={quarter.quarter}
                                handleClick={handleClick}
                                selectedSlice={selectedQuarter}
                                label={month.month}
                            ></SliceMonth>
                        );
                    });
                })}
            </ul>
            <ul className="quarter-level">
                {quarters.map((quarter) => {
                    return (
                        <SliceQuarter
                            key={quarter.quarter}
                            position={position}
                            value={quarter.quarter}
                            handleClick={handleClick}
                            selectedSlice={selectedQuarter}
                        ></SliceQuarter>
                    );
                })}
            </ul>
        </div>
    );
};

const AnnualClockContainer = ({
    quarters,
    position,
}: {
    quarters: AnnualClockQuarter[];
    position?: Position;
}) => {
    const intl = useIntl();
    const today = new Date();
    const quarterStr = "Q" + Math.floor((today.getMonth() + 3) / 3);
    const [selectedSlice, setSelectedSlice] = useState(() => {
        for (let i = 0; i < quarters.length; i++) {
            if (quarters[i]["quarter"] === quarterStr) {
                return quarterStr;
            }
        }
        return null;
    });
    const handleSliceChange = useCallback(
        (_: React.MouseEvent<HTMLButtonElement>, slice: string) => {
            setSelectedSlice(slice);
        },
        [],
    );

    if (!selectedSlice) {
        return null;
    }

    return (
        <div className="AnnualClockContainer">
            <div className="pie-chart">
                <AnnualClockPie
                    position={position}
                    quarters={quarters}
                    selectedQuarter={selectedSlice}
                    handleClick={handleSliceChange}
                />
            </div>
            <div className="event-container">
                {quarters.map((quarter, index) => {
                    if (selectedSlice === quarter.quarter) {
                        return (
                            <div key={index}>
                                <div className="quarter-title">{quarter.quarter}</div>
                                {quarter.months.map((month, i) => {
                                    return (
                                        <div key={i}>
                                            <div className="month-title">{month.description}</div>
                                            {month.events ? (
                                                month.events.map((eventName, num) => {
                                                    return (
                                                        <div key={num}>
                                                            <div className="event">
                                                                <div className="date">
                                                                    {eventName.date}
                                                                </div>

                                                                {eventName.url ? (
                                                                    <LinkButton
                                                                        className="link"
                                                                        href={eventName.url}
                                                                    >
                                                                        {eventName.title}
                                                                    </LinkButton>
                                                                ) : (
                                                                    <div className="event-title">
                                                                        {eventName.title}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="no-activities">
                                                    {intl.formatMessage({
                                                        id: "annual_clock.no_activites",
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    } else {
                        return null;
                    }
                })}
            </div>
        </div>
    );
};
export default AnnualClockContainer;
